import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import apiService from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { SpecialOffer, StoreUser } from '../types';

// 未応答（承諾/辞退/期限切れになっていない）オファーか。
// opened は「演出を確認したが未回答」なので、回答されるまでバナーを出し続ける。
const isPending = (o: SpecialOffer): boolean => o.status === 'sent' || o.status === 'opened';

/**
 * 特別オファー到着バナー（修正依頼 #49）。
 *
 * LINE push を見逃すとオファー到着に気づけないため、未応答の特別オファー / 招待状が
 * ある間は TOP（/jobs）を含む全画面の上部に常時表示し、タップで受信箱（/scout）へ誘導する。
 * 受信箱表示中は二重になるため出さない。
 *
 * 対象 store_user は受信箱（ScoutPage）と同じく全登録店舗を集約する
 * （複数店舗登録ワーカーが他店舗のオファーを取り漏らさないため）。
 * 画面遷移のたびに再取得するので、受信箱で諾否した後に他画面へ戻るとバナーは消える。
 * useNavigate / useLocation を使うため、必ず Router 配下に置くこと。
 */
const SpecialOfferBanner: React.FC = () => {
  const { userId, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState<number>(0);

  const onScoutPage = location.pathname.startsWith('/scout');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!isAuthenticated || !userId || onScoutPage) return;
      try {
        const suRes = await apiService.getStoreUser(userId);
        if (cancelled || !suRes?.success || !suRes.data || suRes.data.length === 0) return;
        const storeUserIds = (suRes.data as StoreUser[]).map((su) => su.id);
        const offersRes = await Promise.all(storeUserIds.map((suId) => apiService.getSpecialOffers(suId)));
        if (cancelled) return;
        const allOffers = offersRes
          .filter((res) => res.success && res.data)
          .flatMap((res) => res.data as SpecialOffer[]);
        setPendingCount(allOffers.filter(isPending).length);
      } catch {
        /* 非クリティカル：バナーを出さないで続行 */
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId, isAuthenticated, location.pathname, onScoutPage]);

  if (pendingCount === 0 || onScoutPage) return null;

  return (
    <Box
      onClick={() => navigate('/scout')}
      role="button"
      aria-label={`特別オファーが${pendingCount}件届いています。タップして確認`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mx: 2,
        mt: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        cursor: 'pointer',
        color: '#fff',
        // 招待状ヒーローカードと同系の紫グラデ（付録C.5）で「特別なお知らせ」を表現
        background: 'linear-gradient(45deg, #7e318e 0%, #534dff 100%)',
        boxShadow: '0 2px 8px rgba(88,86,214,0.35)',
      }}
    >
      <CardGiftcardRoundedIcon sx={{ fontSize: 22 }} />
      <Typography sx={{ fontSize: '13px', fontWeight: 700, flexGrow: 1 }}>
        特別オファーが届いています
      </Typography>
      <Chip
        label={`${pendingCount}件`}
        size="small"
        sx={{
          height: 20,
          fontSize: '11px',
          fontWeight: 800,
          color: '#5b3d94',
          bgcolor: '#fff',
        }}
      />
      <ChevronRightRoundedIcon sx={{ fontSize: 20, opacity: 0.9 }} />
    </Box>
  );
};

export default SpecialOfferBanner;
